import React from 'react';

import {
  Box,
  Button,
  Skeleton,
  Typography,
} from '@mui/material';

import CircleIcon from '@mui/icons-material/Circle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ScienceIcon from '@mui/icons-material/Science';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import EditIcon from '@mui/icons-material/Edit';

import { RuleVersionStatus } from '../../../../hooks/useRuleVersion';

type StatusDisplay = { label: string; icon?: React.ReactNode; color: string };

const statusDisplay: Record<RuleVersionStatus, StatusDisplay> = {
  live: { label: 'Live', icon: <CircleIcon sx={{ fontSize: 12, color: 'emphasisText.main' }} />, color: 'text.primary' },
  previouslyLive: { label: 'Previously live', icon: <RadioButtonUncheckedIcon sx={{ fontSize: 12 }} />, color: 'text.secondary' },
  testActive: { label: 'Test · Active', icon: <ScienceIcon sx={{ fontSize: 22 }} />, color: 'text.primary' },
  testDeactivated: { label: 'Test · Deactivated', icon: <ScienceOutlinedIcon sx={{ fontSize: 22 }} />, color: 'text.secondary' },
  draft: { label: 'Not published', color: 'text.primary' },
};

const VersionHeading = ({
  status,
  number,
  isLoading,
  openDraft,
  openLiveVersion,
}: {
  status?: RuleVersionStatus;
  number?: number;
  isLoading: boolean;
  openDraft: () => void;
  // Undefined when the rule type has no live version to go back to
  openLiveVersion?: () => void;
}) => {
  if (isLoading || !status) {
    return (
      <Box display="flex" alignItems="center" gap={1} data-testid="ruleVersionHeading">
        <Skeleton variant="text" width={160} />
      </Box>
    );
  }

  const { label, icon, color } = statusDisplay[status];
  const isDraft = status === 'draft';

  return (
    <Box
      display="flex"
      alignItems="center"
      flexGrow={1}
      justifyContent="space-between"
      flexWrap="wrap"
      columnGap={2}
      rowGap={1}
      whiteSpace="nowrap"
      data-testid="ruleVersionHeading"
    >
      <Box display="flex" alignItems="baseline" gap={5}>
        <Typography
          component="h2"
          color="text.primary"
          sx={{ fontSize: 24, fontWeight: 600, lineHeight: 1.25 }}
        >
          {isDraft ? 'Draft' : `Version ${number}`}
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          color={color}
          data-testid="ruleVersionStatus"
        >
          {icon}
          <Typography color="inherit" sx={{ fontSize: 18, alignSelf: 'baseline' }}>{label}</Typography>
        </Box>
      </Box>
      {!isDraft && (
        <Button
          variant="text"
          size="small"
          startIcon={<EditIcon />}
          onClick={openDraft}
        >
          Open draft
        </Button>
      )}
      {isDraft && openLiveVersion && (
        <Button
          variant="text"
          size="small"
          // A small button sets its icon's font size, so the dot is sized by width and height
          startIcon={<CircleIcon sx={{ width: 10, height: 10 }} />}
          sx={{ alignItems: 'baseline' }}
          onClick={openLiveVersion}
        >
          Open live version
        </Button>
      )}
    </Box>
  );
};

export default VersionHeading;
